<template>
  <v-container fluid class="dataset-detail" v-if="dataset">
    <v-row class="flex-grow-0">
      <v-btn text color="primary" exact :to="{ name: 'rbu-datasets' }">
        <h3 class="font-weight-regular">
          &lt;&lt; {{ $t("app_drawer.rbu_datasets") }}
        </h3>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        v-show="
          ['ADW_MAIN', 'ADW_2021', 'L_M_INDEX2003', 'L_M_INDEX2021'].includes(
            dataset.data_type
          ) && canSync
        "
        color="primary"
        small
        class="ml-4"
        @click="manuallySyncDataset"
        :disabled="syncingDataset"
        >MANUAL SYNC - {{ dataset.data_type }}
        {{ syncingDataset ? "..." : "" }}
      </v-btn>
      <v-btn color="primary" small class="ml-4" @click="edit">EDIT </v-btn>
      <v-btn color="primary" small class="ml-4" @click="importDataset">{{
        importBtnName
      }}</v-btn>
      <v-btn
        v-if="dataset.data_type === 'CLBM_XML'"
        color="primary"
        small
        class="ml-4"
        @click="importDatasetExcel"
        >IMPORT EXCEL</v-btn
      >
    </v-row>

    <v-row class="dataset-info-wrapper">
      <v-col cols="12">
        <div class="dataset-info">
          <h3>[{{ datasetTypesString }}] {{ dataset.title }}</h3>
          <h5>
            Last Updated At:
            {{
              dataset.last_updated_at
                ? $parseDate(dataset.last_updated_at)
                : "N/A"
            }}
          </h5>
          <h5 v-for="(s, index) in datasetSources" :key="index">
            Sources: <a :href="s" target="_blank">{{ s }}</a>
          </h5>
          <h5>Value Type: {{ dataset.value_type }}</h5>
        </div>
      </v-col>
    </v-row>

    <v-row class="flex-grow-1 rounded-b">
      <DatasetIssueList
        :extra-filters="{ dataset_id: [$route.params.datasetId] }"
        :refresh-trigger.sync="datasetIssueListRefreshTrigger"
      ></DatasetIssueList>
    </v-row>
  </v-container>
</template>
<script>
import { ACTION_TYPES } from "@/store/types";
import { computed, onMounted, ref } from "@vue/composition-api";
import DatasetIssueList from "@/components/dataset/DatasetIssueList.vue";
import ImportDatasetDialog from "@/components/rbu/ImportDatasetDialog.vue";
import ImportDatasetExcelDialog from "@/components/rbu/ImportDatasetExcelDialog.vue";
import EditDatasetDialog from "@/components/dataset/EditDatasetDialog.vue";
import { USER_TYPE, PERMISSIONS } from "@/constants";
import _ from "lodash";

export default {
  name: "Dataset",
  components: { DatasetIssueList },
  setup(props, { root }) {
    const dataset = ref(null);
    const datasetTypesString = computed(() => {
      if (dataset.value) {
        return dataset.value.types.map((t) => t.name).join(", ");
      }

      return "";
    });
    const importBtnName = computed(() => {
      if (!dataset.value) return "Import";
      if (dataset.value.data_type === "CLBM_XML") return "Import XML";
      else return "Import EXCEL";
    });
    const datasetSources = computed(() => {
      if (dataset.value) {
        return (
          {
            ADW_2021: [
              "https://www.censtatd.gov.hk/en/web_table.html?id=220-20001#",
            ],
            ADW_MAIN: [
              "https://www.censtatd.gov.hk/en/web_table.html?id=220-20001A#",
            ],
            L_M_INDEX2003: [
              "https://www.censtatd.gov.hk/en/web_table.html?id=615-21001A",
              "https://www.censtatd.gov.hk/en/web_table.html?id=615-21002A",
            ],
            L_M_INDEX2021: [
              "https://www.censtatd.gov.hk/en/web_table.html?id=615-21001",
              "https://www.censtatd.gov.hk/en/web_table.html?id=615-21002",
            ],
            CLBM_XML: [
              "https://www.censtatd.gov.hk/en/EIndexbySubject.html?pcode=B1060005&scode=330",
            ],
          }[dataset.value.data_type] || []
        );
      }

      return [];
    });
    const getDataset = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/dataset/",
        params: { dataset_id: root.$route.params.datasetId },
      });

      if (response.body.success) {
        dataset.value = response.body.dataset;
      }
    };

    const datasetIssueListRefreshTrigger = ref(false);
    const canImport = computed(() => {
      return (
        dataset.value &&
        ["MANUAL", "CLBM_XML"].indexOf(dataset.value.data_type) !== -1
      );
    });

    const canSync = computed(() => {
      if (!root.$store.getters.account) return false;
      return (
        dataset.value &&
        ["ADW_2021", "ADW_MAIN", "L_M_INDEX2003", "L_M_INDEX2021"].includes(
          dataset.value.data_type
        ) &&
        ([USER_TYPE.SUPER_ADMIN].includes(root.$store.getters.account.type) ||
          _.some([PERMISSIONS.MANAGE_RBU_DATASET], (p) =>
            root.$store.getters.hasPermission(p)
          ))
      );
    });

    const edit = async function () {
      const needRefresh = await root.$dialog.showAndWait(EditDatasetDialog, {
        showClose: false,
        persistent: true,
        dataset: dataset.value,
        width: "800",
      });

      if (needRefresh) {
        getDataset();
      }
    };

    const importDatasetExcel = async function () {
      const needRefresh = await root.$dialog.showAndWait(
        ImportDatasetExcelDialog,
        {
          showClose: false,
          persistent: true,
          dataset: dataset.value,
          width: "800",
        }
      );

      if (needRefresh) {
        datasetIssueListRefreshTrigger.value = true;
      }
    };

    const importDataset = async function () {
      const needRefresh = await root.$dialog.showAndWait(ImportDatasetDialog, {
        showClose: false,
        persistent: true,
        dataset: dataset.value,
        width: "800",
      });

      if (needRefresh) {
        datasetIssueListRefreshTrigger.value = true;
      }
    };

    const syncingDataset = ref(false);
    const manuallySyncDataset = async function () {
      syncingDataset.value = true;
      try {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/dataset/manually_sync/",
          params: { dataset_id: root.$route.params.datasetId },
        });
      } catch (error) {
        console.error(error);
      }
      datasetIssueListRefreshTrigger.value = true;
      syncingDataset.value = false;
    };

    onMounted(() => {
      getDataset();
    });

    return {
      importBtnName,
      dataset,
      datasetTypesString,
      datasetSources,
      canImport,
      canSync,
      edit,
      importDataset,
      datasetIssueListRefreshTrigger,
      importDatasetExcel,
      manuallySyncDataset,
      syncingDataset,
    };
  },
};
</script>
